import {
	faCircleCheck as fasCircleCheck,
	faPaperPlane as fasPaperPlane,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { faCircleCheck as fadCircleCheck } from "@awesome.me/kit-3777c2145e/icons/duotone/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { Config } from "@/app/config";
import type { SubscriptionTypes } from "@/component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import { GoogleAnalytics4 } from "@/shared/lib/google-analytics-4";
import { logError } from "@/shared/lib/sentry-error";
import type { HttpError, ProgressButtonState } from "@/shared/ui";
import { __, http, List, ProgressButton } from "@/shared/ui";
import type { AbstractStepProps, AbstractStepState } from "./AbstractStep";
import { AbstractStep } from "./AbstractStep";

interface Props extends AbstractStepProps {
	title?: string;

	successMessage?: string;
	feedBackEmail?: string;

	userEmail: string;

	withoutSubscribe?: boolean;
	subscriptionTypes: SubscriptionTypes[];

	subscriptionProductTypes?: string[];
}

interface State extends AbstractStepState {
	subscirbeState: ProgressButtonState;
}

export class BasicSuccessPage extends AbstractStep<{}, Props, State> {
	public static defaultProps = {
		...AbstractStep.defaultProps,
		title: __("Köszönjük, hogy leadtad érdeklődésed!"),
		successMessage: "Munkatársunk rövidesen felveszi Veled a kapcsolatot!",
		feedBackEmail: Config.getContactEmail(),
		withoutSubscribe: Config.isPartner(),
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			subscirbeState: undefined,
		};
	}

	protected renderContent(): ReactNode {
		const { title, feedBackEmail, colorVersion, successMessage } = this.props;

		return (
			<>
				<Card.Header>
					<Card.Title className="text-center text-white">{title}</Card.Title>
				</Card.Header>
				<Card.Body
					className={`d-flex flex-column justify-content-center text-white${colorVersion === "black" ? " bg-dark" : ""}`}
				>
					<div className="d-flex px-3 py-4 border-bottom border-gray">
						<div className="text-center my-auto ml-auto">
							{colorVersion === "black" ? (
								<FontAwesomeIcon icon={fadCircleCheck} className="duotone-success" size="5x" />
							) : (
								<FontAwesomeIcon icon={fasCircleCheck} className="text-gold" size="4x" />
							)}
						</div>
						<div className="font-weight-bold my-auto mr-auto ml-3">{successMessage}</div>
					</div>
					<div className="text-center py-3">
						Ha addig is további kérdésed lenne, írj nekünk a{" "}
						<a className="text-reset text-underline" href={`mailto:${feedBackEmail}`}>
							{feedBackEmail}
						</a>{" "}
						címre.
					</div>
				</Card.Body>
				{this.renderSubscribe()}
			</>
		);
	}

	protected renderSubscribe(): ReactNode {
		const { withoutSubscribe, colorVersion } = this.props;
		const { subscirbeState } = this.state;
		const isDarkVersion = colorVersion === "black";

		if (withoutSubscribe) {
			return null;
		}

		return (
			<Card.Body
				className={`newsletter-box-2 p-3 d-flex flex-column justify-content-center${isDarkVersion ? " newsletter-box-dark" : ""}`}
			>
				<div className="h4 px-3 text-center text-uppercase text-white">Ne maradj le hírlevelünkről!</div>
				<List itemStyle="tick" variant={isDarkVersion ? "success" : "gold"} className="text-white">
					{this.renderSubscribeList()}
				</List>
				<div className="text-center">
					<ProgressButton
						className={`mt-2${isDarkVersion ? " font-weight-bold" : ""}`}
						variant={isDarkVersion ? "primary" : "gold"}
						icon={<FontAwesomeIcon icon={fasPaperPlane} />}
						size="lg"
						block={!!colorVersion}
						state={subscirbeState}
						onClick={this.onSubscribeClick}
					>
						Feliratkozás
					</ProgressButton>
				</div>
			</Card.Body>
		);
	}

	protected renderSubscribeList(): ReactNode {
		const { subscriptionTypes = [] } = this.props;

		if (subscriptionTypes.includes("CORPORATE_NEWSLETTER")) {
			return "Iratkozz fel hírlevelünkre, hogy ne maradj le vállalkozásod új lehetőségeiről és a fontos hírekről!";
		}

		return (
			<>
				<List.Item>
					<strong>Legfontosabb változások</strong> a hitel és megtakarítási lehetőségek piacán
				</List.Item>
				<List.Item>
					<strong>Ötletek, javaslatok</strong> hogyan hozz ki többet anyagi helyzetedből
				</List.Item>
			</>
		);
	}

	@autobind
	protected onSubscribeClick(): void {
		const { userEmail, subscriptionTypes, subscriptionProductTypes = undefined } = this.props;
		this.setState({ subscirbeState: "loading" });

		// Feliratkozás
		this.promisePool.add(
			http
				.post(Config.getWpRestApiUrl(`/subscribe/${encodeURIComponent(userEmail)}`), {
					subscriptionTypes,
					productTypes: subscriptionProductTypes,
				})
				.then(() => {
					this.setState({ subscirbeState: "done" });

					this.sendEvent("BM_newsletterSubscription", {
						buttonId: GoogleAnalytics4.generateId("bi"),
						email: userEmail,
						newsletterType: "general",
						user_data: {
							email_address: userEmail,
						},
					});
				})
				.catch((error: HttpError) => {
					this.setState({ subscirbeState: "error" });

					this.promisePool.addTimeout().then(() => {
						this.setState({ subscirbeState: undefined });
					});

					logError(error);
				}),
		);
	}

	protected getClassName(): string {
		const { colorVersion } = this.props;

		return compact([
			super.getClassName(),
			colorVersion === "black" ? undefined : "bg-success",
			"success-step",
		]).join(" ");
	}

	protected getComponentName(): string {
		return "BasicSuccessPage";
	}
}
